import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { mapDbMissionToMissionDetails } from '../lib/mappers';
import type { MissionDetails } from '../types';

export type PortfolioScope = 'portfolio' | 'org';

// Light columns for list/kanban — no large blobs.
// location_city / tjm_min / tjm_max sont les colonnes dédiées alimentées par le
// trigger `missions_extract_list_fields` : sans elles, les cartes affichaient
// « Non communiqué » alors que la fiche (qui charge summary_json) montrait la donnée.
const LIST_SELECT = 'id, mission_title, client_name, status, created_at, updated_at, assigned_to, created_by, client_id, contact_id, location_city, tjm_min, tjm_max';
// Full columns including summary_json + original_jd_text — fetched only for detail view
const DETAIL_SELECT = `${LIST_SELECT}, summary_json, original_jd_text`;

interface UseMissionsResult {
    missions: MissionDetails[];
    isLoading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    updateMissionStatus: (id: string, status: string) => Promise<void>;
    fetchMissionDetail: (id: string) => Promise<MissionDetails | null>;
    deleteMission: (id: string) => Promise<void>;
}

export const useMissions = (scope: PortfolioScope = 'org', userId?: string): UseMissionsResult => {
    const [missions, setMissions] = useState<MissionDetails[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchMissions = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            let query = supabase
                .from('missions')
                .select(LIST_SELECT)
                .order('created_at', { ascending: false });

            if (scope === 'portfolio' && userId) {
                query = query.or(`created_by.eq.${userId},assigned_to.eq.${userId}`);
            }

            const { data, error } = await query;
            if (error) throw error;

            const mappedMissions = (data || [])
                .map(mapDbMissionToMissionDetails)
                .filter((m): m is MissionDetails => m !== null);
            setMissions(mappedMissions);
        } catch (err: any) {
            console.error('Error fetching missions:', err);
            setError(err.message || 'Failed to fetch missions');
        } finally {
            setIsLoading(false);
        }
    }, [scope, userId]);

    const fetchMissionDetail = useCallback(async (id: string): Promise<MissionDetails | null> => {
        const { data, error: dbError } = await supabase
            .from('missions')
            .select(DETAIL_SELECT)
            .eq('id', Number(id))
            .single();
        if (dbError || !data) return null;
        return mapDbMissionToMissionDetails(data as any);
    }, []);

    const updateMissionStatus = useCallback(async (id: string, newStatus: string) => {
        try {
            setMissions(prev => prev.map(m => m.id === id ? { ...m, status: newStatus as any } : m));

            const { error: updateError } = await supabase
                .from('missions')
                .update({ status: newStatus } as any)
                .eq('id', Number(id));

            if (updateError) throw updateError;
        } catch (err: any) {
            console.error('Error updating mission status:', err);
            setError(err.message || 'Failed to update mission status');
            fetchMissions();
        }
    }, [fetchMissions]);

    const deleteMission = useCallback(async (id: string) => {
        try {
            setMissions(prev => prev.filter(m => m.id !== id));
            // Delete dependent records first to avoid FK constraint violations
            await supabase.from('matches').delete().eq('mission_id', Number(id));
            await supabase.from('matching_tasks').delete().eq('mission_id', Number(id));
            const { error: deleteError } = await supabase
                .from('missions')
                .delete()
                .eq('id', Number(id));

            if (deleteError) throw deleteError;
        } catch (err: any) {
            console.error('Error deleting mission:', err);
            setError(err.message || 'Failed to delete mission');
            fetchMissions();
            throw err;
        }
    }, [fetchMissions]);

    useEffect(() => {
        fetchMissions();
    }, [fetchMissions]);

    return { missions, isLoading, error, refresh: fetchMissions, updateMissionStatus, fetchMissionDetail, deleteMission };
};
